import React, { useEffect, useState } from 'react';
import { profileApi } from '../api/client';
import type { Profile as ProfileType } from '../api/client';
import './Profile.css';

const Profile: React.FC = () => {
  const [profile, setProfile] = useState<ProfileType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [formData, setFormData] = useState<Partial<ProfileType>>({});

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      const response = await profileApi.getProfile();
      setProfile(response.data);
      setFormData(response.data);
    } catch (err) {
      setError('Failed to load profile');
      console.error('Profile error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!formData) return;
    
    try {
      const response = await profileApi.updateProfile(formData);
      setProfile(response.data);
      setEditing(false);
      setError(null);
    } catch (err) {
      setError('Failed to update profile');
      console.error('Update profile error:', err);
    }
  };

  const handleCancel = () => {
    setFormData(profile || {});
    setEditing(false);
  };

  const handleInputChange = (field: keyof ProfileType, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleArrayChange = (field: 'interests' | 'personalityTraits', value: string) => {
    const items = value.split(',').map(item => item.trim()).filter(item => item);
    handleInputChange(field, items);
  };

  if (loading) {
    return <div className="profile-page"><div className="loading">Loading profile...</div></div>;
  }

  if (error && !profile) {
    return <div className="profile-page"><div className="error">Error: {error}</div></div>;
  }

  return (
    <div className="profile-page">
      <header className="page-header">
        <h1>👤 Profile</h1>
        <div className="header-actions">
          {!editing ? (
            <button className="btn btn-primary" onClick={() => setEditing(true)}>
              Edit Profile
            </button>
          ) : (
            <div className="edit-actions">
              <button className="btn btn-success" onClick={handleSave}>
                Save Changes
              </button>
              <button className="btn btn-secondary" onClick={handleCancel}>
                Cancel
              </button>
            </div>
          )}
        </div>
      </header>

      {error && <div className="error-message">{error}</div>}

      <div className="profile-content">
        <div className="profile-card">
          <h2>Personal Information</h2>
          
          <div className="form-group">
            <label>Name</label>
            {editing ? (
              <input
                type="text"
                value={formData.name || ''}
                onChange={(e) => handleInputChange('name', e.target.value)}
                className="form-input"
              />
            ) : (
              <div className="form-value">{profile?.name || 'Not specified'}</div>
            )}
          </div>

          <div className="form-group">
            <label>Profession</label>
            {editing ? (
              <input
                type="text"
                value={formData.profession || ''}
                onChange={(e) => handleInputChange('profession', e.target.value)}
                className="form-input"
              />
            ) : (
              <div className="form-value">{profile?.profession || 'Not specified'}</div>
            )}
          </div>

          <div className="form-group">
            <label>Location</label>
            {editing ? (
              <input
                type="text"
                value={formData.location || ''}
                onChange={(e) => handleInputChange('location', e.target.value)}
                className="form-input"
              />
            ) : (
              <div className="form-value">{profile?.location || 'Not specified'}</div>
            )}
          </div>

          <div className="form-group">
            <label>Favorite Color</label>
            {editing ? (
              <input
                type="text"
                value={formData.favoriteColor || ''}
                onChange={(e) => handleInputChange('favoriteColor', e.target.value)}
                className="form-input"
              />
            ) : (
              <div className="form-value">{profile?.favoriteColor || 'Not specified'}</div>
            )}
          </div>

          <div className="form-group">
            <label>Interests</label>
            {editing ? (
              <textarea
                value={formData.interests?.join(', ') || ''}
                onChange={(e) => handleArrayChange('interests', e.target.value)}
                className="form-textarea"
                placeholder="Enter interests separated by commas"
                rows={3}
              />
            ) : (
              <div className="form-value">
                {profile?.interests && profile.interests.length > 0 ? (
                  <div className="tags">
                    {profile.interests.map((interest, index) => (
                      <span key={index} className="tag">{interest}</span>
                    ))}
                  </div>
                ) : (
                  <span>No interests specified</span>
                )}
              </div>
            )}
          </div>

          <div className="form-group">
            <label>Personality Traits</label>
            {editing ? (
              <textarea
                value={formData.personalityTraits?.join(', ') || ''}
                onChange={(e) => handleArrayChange('personalityTraits', e.target.value)}
                className="form-textarea"
                placeholder="Enter personality traits separated by commas"
                rows={3}
              />
            ) : (
              <div className="form-value">
                {profile?.personalityTraits && profile.personalityTraits.length > 0 ? (
                  <div className="tags">
                    {profile.personalityTraits.map((trait, index) => (
                      <span key={index} className="tag trait-tag">{trait}</span>
                    ))}
                  </div>
                ) : (
                  <span>No personality traits specified</span>
                )}
              </div>
            )}
          </div>
        </div>

        {profile?.preferences && profile.preferences.length > 0 && (
          <div className="profile-card">
            <h2>Preferences</h2>
            <div className="preferences-grid">
              {profile.preferences.map((pref, index) => (
                <div key={index} className="preference-item">
                  <div className="preference-header">
                    <span className="preference-name">{pref.name}</span>
                    <span className={`preference-priority priority-${pref.priority}`}>
                      {pref.priority}
                    </span>
                  </div>
                  <div className="preference-category">{pref.category}</div>
                  {pref.description && (
                    <div className="preference-description">{pref.description}</div>
                  )}
                  {pref.tags && pref.tags.length > 0 && (
                    <div className="preference-tags">
                      {pref.tags.map((tag, tagIndex) => (
                        <span key={tagIndex} className="tag small-tag">{tag}</span>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Profile;